import {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {App as AntdApp, Alert, Empty, Popconfirm, Select, Table, Tag, Tooltip} from "antd";
import {Button, FontAwesomeIcon, Loading} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetContentItemsQuery,
	useGetContentPlansQuery,
	useCreateContentItemMutation,
	useDeleteContentItemMutation,
} from "~/reduxs/api/contentApiSlice";
import ContentNewModal from "./ContentNewModal";
import ContentViewer from "./ContentViewer";
import {statusMeta} from "./statusMeta";

const nf = (n) => Number(n || 0).toLocaleString('vi-VN');

/**
 * Tab "Bài viết": danh sách bài AI của dự án + nút viết bài mới. Bài đang chạy nền →
 * poll danh sách 5s; xem/duyệt/sửa từng bài trong ContentViewer (drawer).
 */
function ContentItemsTab({projectId}) {

	const {message} = AntdApp.useApp();
	const [openNew, setOpenNew] = useState(false);
	const [viewId, setViewId] = useState(null);
	const [planFilter, setPlanFilter] = useState(null); // null = tất cả · 0 = bài lẻ · id = 1 kế hoạch

	// Poll danh sách 5s CHỈ khi còn bài đang chạy nền.
	const [pollMs, setPollMs] = useState(0);
	const {data, isLoading} = useGetContentItemsQuery(projectId, {
		pollingInterval: pollMs,
		skipPollingIfUnfocused: true,
	});

	useEffect(() => {
		setPollMs((data?.items || []).some((i) => i.running) ? 5000 : 0);
	}, [data]);

	const [createItem, {isLoading: creating}] = useCreateContentItemMutation();
	const [deleteItem] = useDeleteContentItemMutation();

	const {data: planData} = useGetContentPlansQuery(projectId);

	if (isLoading) return <Loading />;

	const allItems = data?.items || [];
	const items = planFilter === null ? allItems : allItems.filter((i) => (i.plan_id || 0) === planFilter);
	const quota = data?.quota || {};
	const available = Boolean(data?.available);
	const plans = planData?.plans || [];

	const handleCreate = async (values) => {
		try {
			const res = await createItem({projectId, ...values}).unwrap();
			setOpenNew(false);
			message.success('Đã xếp hàng bài viết — AI sẽ xử lý trong nền');
			if (res?.id) setViewId(res.id);
		} catch (e) {
			message.error(rtkErrorMessage(e));
		}
	};

	const handleDelete = async (item) => {
		try {
			await deleteItem({projectId, itemId: item.id}).unwrap();
			message.success('Đã xoá bài viết');
		} catch (e) {
			message.error(rtkErrorMessage(e));
		}
	};

	const columns = [
		{
			title: 'Bài viết',
			dataIndex: 'keyword',
			render: (_, r) => (
				<div style={{cursor: 'pointer'}} onClick={() => setViewId(r.id)}>
					<div style={{fontWeight: 600}}>{r.title || r.keyword}</div>
					<div style={{fontSize: 12.5, color: '#6b7280'}}>
						Từ khoá: {r.keyword}
						{r.intent_source === 'serp' && <Tag style={{marginLeft: 8}} color="blue">Intent SERP thật</Tag>}
					</div>
				</div>
			),
		},
		{
			title: 'Trạng thái',
			dataIndex: 'status',
			width: 210,
			render: (_, r) => {
				const meta = statusMeta(r.status);
				const progress = r.status === 'write' && r.sections_total > 0 ? ` ${r.write_cursor}/${r.sections_total}` : '';
				// Bài trong kế hoạch có lịch trải — chưa tới hạn thì hiện ngày hẹn thay vì "đang chạy".
				const waiting = r.status === 'intent' && r.scheduled_at && new Date(r.scheduled_at.replace(' ', 'T')) > new Date();
				return (
					<>
						{waiting
							? <Tag>Hẹn {r.scheduled_at.slice(5, 10).split('-').reverse().join('/')}</Tag>
							: <Tag color={meta.color}>{meta.label}{progress}</Tag>}
						{r.plan_id > 0 && <Tag color="purple">KH #{r.plan_id}</Tag>}
						{r.error && (
							<Tooltip title={r.error}>
								<FontAwesomeIcon icon="fa-light fa-circle-exclamation" style={{color: '#dc2626'}} />
							</Tooltip>
						)}
					</>
				);
			},
		},
		{
			title: 'Điểm SEO',
			dataIndex: 'checklist_score',
			width: 100,
			responsive: ['md'],
			render: (v, r) => (['review', 'done'].includes(r.status) || v > 0)
				? <span style={{fontWeight: 600, color: v >= 75 ? '#16a34a' : '#f59e0b'}}>{v}/100</span>
				: '—',
		},
		{
			title: 'Token',
			dataIndex: 'tokens',
			width: 100,
			responsive: ['lg'],
			render: (v) => (v > 0 ? nf(v) : '—'),
		},
		{
			// Đợt 3: hiệu quả bài ĐÃ ĐĂNG từ GSC 28 ngày (cần dự án đã kết nối GSC).
			title: 'GSC 28 ngày',
			key: 'gsc',
			width: 150,
			responsive: ['lg'],
			render: (_, r) => {
				if (r.status !== 'published') return '—';
				if (!r.gsc) return <Tooltip title="Chưa có dữ liệu GSC cho URL này (cần kết nối GSC + chờ Google ghi nhận)."><span>chưa có dữ liệu</span></Tooltip>;
				return (
					<Tooltip title={`${nf(r.gsc.clicks)} clicks · ${nf(r.gsc.impressions)} hiển thị · vị trí TB ${r.gsc.position}`}>
						<span><b>{nf(r.gsc.clicks)}</b> clicks · #{r.gsc.position}</span>
					</Tooltip>
				);
			},
		},
		{
			title: 'Cập nhật',
			dataIndex: 'updated',
			width: 150,
			responsive: ['lg'],
			render: (v, r) => v || r.created,
		},
		{
			title: '',
			key: 'actions',
			width: 90,
			render: (_, r) => (
				<div style={{display: 'flex', gap: 10, justifyContent: 'flex-end'}}>
					{r.status === 'published' && r.external_url && (
						<Tooltip title="Mở bài trên kênh">
							<a href={r.external_url} target="_blank" rel="noreferrer">
								<FontAwesomeIcon icon="fa-light fa-arrow-up-right-from-square" />
							</a>
						</Tooltip>
					)}
					<Tooltip title="Xem / duyệt bài">
						<span style={{cursor: 'pointer'}} onClick={() => setViewId(r.id)}>
							<FontAwesomeIcon icon="fa-light fa-eye" />
						</span>
					</Tooltip>
					<Popconfirm title="Xoá bài viết này?" okText="Xoá" cancelText="Huỷ" onConfirm={() => handleDelete(r)}>
						<span style={{cursor: 'pointer', color: '#dc2626'}}>
							<FontAwesomeIcon icon="fa-light fa-trash-can" />
						</span>
					</Popconfirm>
				</div>
			),
		},
	];

	return (
		<div>
			{!available && (
				<Alert
					type="warning" showIcon style={{marginBottom: 12}}
					message="Chưa khởi tạo bảng dữ liệu"
					description="Chạy GET api/utils/database để tạo bảng content_items/content_settings trước khi dùng tool này."
				/>
			)}

			{available && quota.can === false && (
				<Alert
					type="info" showIcon style={{marginBottom: 12}}
					message="Gói hiện tại không có Nội dung AI tự động"
					description="Nâng cấp lên gói Chuyên nghiệp trở lên để AI tự lên dàn ý và viết bài trong nền."
				/>
			)}

			<div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap'}}>
				<div style={{display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap'}}>
					{plans.length > 0 && (
						<Select
							allowClear
							placeholder="Lọc theo kế hoạch"
							style={{minWidth: 200}}
							value={planFilter}
							onChange={(v) => setPlanFilter(v === undefined ? null : v)}
							options={[
								{value: 0, label: 'Bài lẻ (ngoài kế hoạch)'},
								...plans.map((p) => ({value: p.id, label: p.name})),
							]}
						/>
					)}
					<span style={{color: '#6b7280', fontSize: 13}}>
						{quota.limit > 0 && (
							<>
								Còn <b>{nf(quota.remaining)}</b> / {nf(quota.limit)} bài trong chu kỳ
								{(quota.extra || 0) > 0 && <> (gồm {nf(quota.extra)} bài Extra)</>}
							</>
						)}
						{/* Hết quota nhưng gói VẪN có tính năng → gợi ý mua Gói Extra thay vì nâng gói. */}
						{quota.remaining != null && quota.remaining <= 0 && quota.can !== false && (
							<> · <Link to="/subscription">Mua thêm Gói Extra →</Link></>
						)}
					</span>
				</div>
				<Button
					primary small
					leftIcon={<FontAwesomeIcon icon="fa-light fa-feather-pointed" />}
					disabled={!available || quota.can === false || (quota.remaining != null && quota.remaining <= 0)}
					onClick={() => setOpenNew(true)}
				>
					Viết bài mới
				</Button>
			</div>

			{items.length === 0 ? (
				<Empty description="Chưa có bài viết nào — bấm 'Viết bài mới' để AI bắt đầu." />
			) : (
				<div className="table-scroll">
					<Table rowKey="id" dataSource={items} columns={columns} pagination={{pageSize: 10, hideOnSinglePage: true}} scroll={{x: 760}} />
				</div>
			)}

			<ContentNewModal open={openNew} submitting={creating} onCancel={() => setOpenNew(false)} onSubmit={handleCreate} />

			<ContentViewer projectId={projectId} itemId={viewId} onClose={() => setViewId(null)} />
		</div>
	);
}

export default ContentItemsTab;
